import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { GuestCatalogLayout } from "@/components/guest-catalog/GuestCatalogLayout";
import { GuestCatalogHighlights } from "@/components/guest-catalog/GuestCatalogHighlights";
import { GuestCatalogDigitalSection } from "@/components/guest-catalog/GuestCatalogDigitalSection";
import { GuestCatalogResults } from "@/components/guest-catalog/GuestCatalogResults";
import { GuestCatalogStickyToolbar } from "@/components/guest-catalog/GuestCatalogStickyToolbar";
import { parseGuestCatalogType, useGuestOpacTenantSlug } from "@/hooks/useGuestOpacTenantSlug";
import { useGuestOpacBranding } from "@/hooks/useGuestOpacBranding";
import { useGuestCatalogFeed } from "@/hooks/useGuestCatalogFeed";
import { useGuestCatalogViewMode } from "@/hooks/useGuestCatalogViewMode";
import type { GuestCatalogType } from "@/hooks/useGuestOpacTenantSlug";

export function GuestCatalogPage() {
  const { isLoading: tenantLoading } = useTenant();
  const tenantSlug = useGuestOpacTenantSlug();
  const branding = useGuestOpacBranding(tenantSlug);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const { viewMode, setViewMode } = useGuestCatalogViewMode(tenantSlug);

  const type = parseGuestCatalogType(searchParams.get("type"));
  const searchQuery = (searchParams.get("q") ?? "").trim();
  const showBrowseSections = !searchQuery && type === "all";

  const { items, total, loading, loadingMore, error, hasMore, loadMore } = useGuestCatalogFeed({
    tenantSlug,
    searchQuery,
    type,
    enabled: Boolean(tenantSlug) && !tenantLoading,
  });

  useEffect(() => {
    setQuery(searchParams.get("q") ?? "");
  }, [searchParams]);

  const updateParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next);
  };

  const catalogLoading = tenantLoading || branding.isLoading || (loading && !!tenantSlug);
  const showCatalogBody = Boolean(tenantSlug) && !tenantLoading && !branding.isLoading && !error;

  return (
    <GuestCatalogLayout libraryName={branding.libraryName} logoUrl={branding.logoUrl}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Explore the catalog</h1>
          <p className="mt-1 text-sm text-gray-500">
            Browse and search published library resources.
          </p>
        </div>

        <GuestCatalogStickyToolbar
          query={query}
          type={type}
          viewMode={viewMode}
          disabled={!tenantSlug || tenantLoading}
          showViewToggle={showCatalogBody && !catalogLoading}
          onQueryChange={setQuery}
          onTypeChange={(value: GuestCatalogType) =>
            updateParams((next) => next.set("type", value))
          }
          onViewModeChange={setViewMode}
          onSubmit={(e) => {
            e.preventDefault();
            updateParams((next) => {
              if (query.trim()) next.set("q", query.trim());
              else next.delete("q");
            });
          }}
        />

        {!tenantSlug ? (
          <p className="py-16 text-center text-sm text-gray-500">
            Library catalog is unavailable. Open your library&apos;s portal to browse the
            collection.
          </p>
        ) : error ? (
          <p className="py-16 text-center text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : (
          <div className="space-y-8">
            {showBrowseSections && tenantSlug && (
              <GuestCatalogHighlights
                tenantSlug={tenantSlug}
                viewMode={viewMode}
                loading={catalogLoading}
              />
            )}
            {showBrowseSections && tenantSlug && (
              <GuestCatalogDigitalSection
                tenantSlug={tenantSlug}
                viewMode={viewMode}
                loading={catalogLoading}
              />
            )}
            <GuestCatalogResults
              items={items}
              total={total}
              viewMode={viewMode}
              hasMore={hasMore}
              loadingMore={loadingMore}
              loading={catalogLoading}
              searchQuery={searchQuery}
              type={type}
              tenantSlug={tenantSlug}
              onLoadMore={loadMore}
            />
          </div>
        )}
      </div>
    </GuestCatalogLayout>
  );
}
